import { memo, useEffect, useState } from "react";

import { DataGridPremium } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { Finding } from "stores/FindingStore";

import { GenericObject } from "utility/misc";

import {
  DataGridStyle,
  FindingsColumns,
  FindingsColumnsLoading,
  LoadingRows,
  PaperStyle,
} from "./data";

// -----------------------------------------------------------------------------

const PageSize = 20;
const PageSizeOptions = [PageSize];
const DataComponents: GenericObject = {
  LoadingOverlay: NullComponent,
};
enum SORT_BY {
  ASCENDING = "ASCENDING",
  DESCENDING = "DESCENDING",
}

// -----------------------------------------------------------------------------

const IAMFindingsDataTable = memo(function IAMFindingsDataTable({
  data,
  isLoading,
}: IAMFindingsDataTableProps): JSX.Element {
  const [displayFindings, setDisplayFindings] = useState(data);
  const [sortBy, setSortBy] = useState<SORT_BY>(SORT_BY.ASCENDING);

  useEffect(() => {
    const sortedData = [...data].sort((a: Finding, b: Finding) => {
      return (
        (dayjs(b.event_time).valueOf() - dayjs(a.event_time).valueOf()) *
        (sortBy === SORT_BY.ASCENDING ? -1 : 1)
      );
    });
    setDisplayFindings(sortedData);
  }, [data, sortBy]);

  const handleChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as SORT_BY);
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom="16px"
      >
        <Grid item>
          <Typography variant="h6" fontWeight="bold">
            IAM Findings
          </Typography>
          <Typography gutterBottom variant="caption" fontWeight="light">
            Showing data as of {dayjs().format("MM/DD/YYYY")}
          </Typography>
        </Grid>
        <Grid item paddingTop={0}>
          <Select
            id="sort-by-select"
            value={sortBy}
            inputProps={{ "aria-label": "Sort by order" }}
            onChange={handleChange}
          >
            <MenuItem value={SORT_BY.ASCENDING}>Sort By: Ascending</MenuItem>
            <MenuItem value={SORT_BY.DESCENDING}>Sort By: Descending</MenuItem>
          </Select>
        </Grid>
      </Grid>
      <Paper elevation={1} sx={PaperStyle}>
        <DataGridPremium
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableSelectionOnClick
          density="compact"
          getRowId={getRowId}
          rows={isLoading ? LoadingRows : displayFindings}
          rowCount={displayFindings.length}
          columns={isLoading ? FindingsColumnsLoading : FindingsColumns}
          components={DataComponents}
          pageSize={PageSize}
          rowsPerPageOptions={PageSizeOptions}
          checkboxSelection={false}
          sx={DataGridStyle}
          loading={isLoading}
        />
      </Paper>
    </>
  );
});

export default IAMFindingsDataTable;

// -----------------------------------------------------------------------------

function getRowId(row: GenericObject): string {
  return row.uuid;
}

function NullComponent() {
  return <></>;
}

interface IAMFindingsDataTableProps {
  data: Finding[];
  isLoading: boolean;
}
